import React, { useState } from 'react';
import { SkeletonBlock } from '@/components/general/skeletonBlock/SkeletonBlock';
import {
    Alert,
    Avatar,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@mui/material';
import { usePhrase } from '@/hooks';
import { mutate } from 'swr';
import { VerificationDocument, VerificationInfo } from '@/types/types';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { config } from '@/config';
import { verificationService } from '@/services/api';
import { RemoveDocumentDialog } from './RemoveDocumentDialog/RemoveDocumentDialog';
import { VERIFICATION_DOCUMENTS_PHRASES } from '@/consts/phrases/verificationPhrases';
import { FileDropZone } from '@/components/forms/inputs/FileDropZone/FileDropZone';
import { isEmpty } from '@/utils/fp';
import { getErrorPhraseIds } from '@/utils/fetch/Fetcher/Fetcher';
import { isArrayWithValues } from '@/utils/fp/array/array';

const API_URL = config.api.url.verification;
const MAX_FILES = 10;
const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
];

interface DocumentsFormProps {
    requestToken: string;
    verificationInfo: VerificationInfo;
    isEnabled: boolean;
}

interface DocumentMessage {
    type: 'success' | 'error';
    phraseId: number | number[];
    replaceTextVars?: (number | string)[];
}

export const DocumentsForm = (props: DocumentsFormProps) => {
    const { t } = usePhrase();
    const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
    const [documentToRemove, setDocumentToRemove] = useState<VerificationDocument>();
    const [message, setMessage] = useState<DocumentMessage | null>(null);
    const [documents, setDocuments] = useState<VerificationDocument[]>(
        props.verificationInfo.documents,
    );

    const deleteDocument = async (id: number) => {
        try {
            await verificationService.deleteDocumentById(props.requestToken, id);
            setDocuments(documents.filter((document) => document.id !== id));
            setMessage({
                type: 'success',
                phraseId: VERIFICATION_DOCUMENTS_PHRASES.REMOVE_SUCCESS,
            });
        } catch (e) {
            const removalErrorPhraseIds =
                getErrorPhraseIds(e) ?? VERIFICATION_DOCUMENTS_PHRASES.REMOVE_ERROR;
            setMessage({
                type: 'error',
                phraseId: removalErrorPhraseIds,
            });
        }
    };

    const deleteDocumentWithDialog = (document: VerificationDocument) => {
        setDocumentToRemove(document);
        setOpenRemoveDialog(true);
    };

    const uploadDocuments = async (files: File[]) => {
        if (files.length + documents.length > MAX_FILES) {
            setMessage({
                type: 'error',
                phraseId: VERIFICATION_DOCUMENTS_PHRASES.UPLOAD_MORE_THAN_ALLOWED,
                replaceTextVars: [MAX_FILES],
            });
            return;
        }

        if (files.filter((file) => SUPPORTED_FILE_TYPES.includes(file.type)).length === 0) {
            setMessage({
                type: 'error',
                phraseId: VERIFICATION_DOCUMENTS_PHRASES.UPLOAD_NOT_SUPPORTED,
                replaceTextVars: [['docx', 'jpeg', 'pdf', 'png'].join(', ')],
            });
            return;
        }

        try {
            const updatedDocuments = await verificationService.uploadDocuments(
                props.requestToken,
                files,
            );
            setDocuments(updatedDocuments);
            setMessage({
                type: 'success',
                phraseId: VERIFICATION_DOCUMENTS_PHRASES.UPLOAD_SUCCESS,
            });
            await mutate(`${API_URL}/${props.requestToken}`);
        } catch (e) {
            const uploadingErrorPhraseIds =
                getErrorPhraseIds(e) ?? VERIFICATION_DOCUMENTS_PHRASES.UPLOAD_ERROR;
            setMessage({
                type: 'error',
                phraseId: uploadingErrorPhraseIds,
            });
        }
    };

    return (
        <>
            <Typography variant="h5" sx={{ pb: 1 }}>
                {t(VERIFICATION_DOCUMENTS_PHRASES.TITLE)}
            </Typography>
            <List>
                <RemoveDocumentDialog
                    open={openRemoveDialog}
                    setOpen={setOpenRemoveDialog}
                    document={documentToRemove}
                    removeDocument={deleteDocument}
                />
                {message && (
                    <Alert
                        sx={{ mb: '1rem' }}
                        severity={message.type}
                        data-testid={`message-${message.type}`}
                    >
                        {isArrayWithValues(message.phraseId) ? (
                            <>
                                {message.phraseId.map((p, index) => (
                                    <Box sx={{ mt: index ? 1 : 0 }} key={p}>
                                        {t(p, { replaceTextVars: message.replaceTextVars })}
                                    </Box>
                                ))}
                            </>
                        ) : (
                            t(message.phraseId, { replaceTextVars: message.replaceTextVars })
                        )}
                    </Alert>
                )}
                {!isEmpty(documents) &&
                    documents.map((document, index) => (
                        <DocumentItem
                            document={document}
                            key={index}
                            deleteDocument={deleteDocumentWithDialog}
                            deleteEnabled={props.isEnabled}
                        />
                    ))}
                {isEmpty(documents) && (
                    <Alert severity="info" data-testid="empty-documents">
                        {t(VERIFICATION_DOCUMENTS_PHRASES.EMPTY_LIST)}
                    </Alert>
                )}
                {documents?.length < MAX_FILES && (
                    <FileDropZone isDisabled={!props.isEnabled} upload={uploadDocuments} />
                )}
            </List>
        </>
    );
};

interface DocumentProps {
    document: VerificationDocument;
    deleteDocument: (document: VerificationDocument) => void;
    deleteEnabled: boolean;
}

const DocumentItem = (props: DocumentProps) => {
    const { filename, link, uploaded } = props.document;
    const uploadDate = format(new Date(uploaded), config.dateFormat);
    const deleteDocument = () => {
        props.deleteDocument(props.document);
    };
    return (
        <ListItem>
            <ListItemButton
                component="a"
                href={link}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
            >
                <ListItemAvatar>
                    <Avatar>
                        <InsertDriveFileIcon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText primary={filename} secondary={uploadDate} />
            </ListItemButton>
            {props.deleteEnabled && (
                <ListItemSecondaryAction>
                    <IconButton
                        data-testid="remove-document-button"
                        aria-label="remove"
                        edge="end"
                        onClick={deleteDocument}
                    >
                        <DeleteIcon />
                    </IconButton>
                </ListItemSecondaryAction>
            )}
        </ListItem>
    );
};

export const DocumentsFormSkeleton = () => (
    <>
        <SkeletonBlock amount={2} height={60} />
        <SkeletonBlock height={256} />
    </>
);
